const API_BASE = process.env.NEXT_PUBLIC_API_URL || "http://localhost:8000/api/v1";

class ApiClient {
  private token: string | null = null;

  setToken(token: string) {
    this.token = token;
    if (typeof window !== "undefined") {
      localStorage.setItem("access_token", token);
    }
  }

  getToken(): string | null {
    if (!this.token && typeof window !== "undefined") {
      this.token = localStorage.getItem("access_token");
    }
    return this.token;
  }

  clearToken() {
    this.token = null;
    if (typeof window !== "undefined") {
      localStorage.removeItem("access_token");
      localStorage.removeItem("refresh_token");
    }
  }

  private async request<T>(
    path: string,
    options: RequestInit = {}
  ): Promise<T> {
    const token = this.getToken();
    const headers: Record<string, string> = {
      ...(options.headers as Record<string, string>),
    };

    if (token) {
      headers["Authorization"] = `Bearer ${token}`;
    }

    if (!(options.body instanceof FormData)) {
      headers["Content-Type"] = "application/json";
    }

    const response = await fetch(`${API_BASE}${path}`, {
      ...options,
      headers,
    });

    if (response.status === 401) {
      this.clearToken();
      if (typeof window !== "undefined") {
        window.location.href = "/login";
      }
      throw new Error("Nicht autorisiert");
    }

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: "Ein Fehler ist aufgetreten" }));
      throw new Error(error.detail || `HTTP ${response.status}`);
    }

    if (response.status === 204) {
      return undefined as T;
    }

    return response.json();
  }

  // Auth
  async login(email: string, password: string, mfaCode?: string) {
    const result = await this.request<{
      access_token: string;
      refresh_token: string;
    }>("/auth/login", {
      method: "POST",
      body: JSON.stringify({ email, password, mfa_code: mfaCode }),
    });
    this.setToken(result.access_token);
    if (typeof window !== "undefined") {
      localStorage.setItem("refresh_token", result.refresh_token);
    }
    return result;
  }

  async getMe() {
    return this.request<User>("/auth/me");
  }

  // Clients
  async getClientCount() {
    return this.request<{ total: number }>("/clients/count");
  }

  async getClients(search?: string) {
    const params = search ? `?search=${encodeURIComponent(search)}` : "";
    return this.request<Client[]>(`/clients/${params}`);
  }

  async getClient(id: string) {
    return this.request<Client>(`/clients/${id}`);
  }

  async createClient(data: Partial<Client>) {
    return this.request<Client>("/clients/", {
      method: "POST",
      body: JSON.stringify(data),
    });
  }

  async updateClient(id: string, data: Partial<Client>) {
    return this.request<Client>(`/clients/${id}`, {
      method: "PATCH",
      body: JSON.stringify(data),
    });
  }

  // Tax Returns
  async getTaxReturns(clientId?: string, taxYear?: number) {
    const params = new URLSearchParams();
    if (clientId) params.set("client_id", clientId);
    if (taxYear) params.set("tax_year", taxYear.toString());
    const query = params.toString() ? `?${params.toString()}` : "";
    return this.request<TaxReturn[]>(`/tax-returns/${query}`);
  }

  async getTaxReturn(id: string) {
    return this.request<TaxReturn>(`/tax-returns/${id}`);
  }

  async createTaxReturn(clientId: string, taxYear: number) {
    return this.request<TaxReturn>("/tax-returns/", {
      method: "POST",
      body: JSON.stringify({ client_id: clientId, tax_year: taxYear }),
    });
  }

  async updateTaxReturn(id: string, data: Partial<TaxReturn>) {
    return this.request<TaxReturn>(`/tax-returns/${id}`, {
      method: "PATCH",
      body: JSON.stringify(data),
    });
  }

  async deleteTaxReturn(id: string) {
    return this.request<void>(`/tax-returns/${id}`, {
      method: "DELETE",
    });
  }

  // Sub-Forms (Unterformulare)
  async getSubForm(taxReturnId: string, formType: string) {
    return this.request<Record<string, unknown>>(`/tax-returns/${taxReturnId}/sub-form/${formType}`);
  }

  async saveSubForm(taxReturnId: string, formType: string, data: Record<string, unknown>) {
    return this.request<{ sub_form: Record<string, unknown>; field_updates: Record<string, number> }>(
      `/tax-returns/${taxReturnId}/sub-form/${formType}`,
      {
        method: "PATCH",
        body: JSON.stringify(data),
      }
    );
  }

  // Documents — Upload (Legacy + Batch)
  async uploadDocument(taxReturnId: string, file: File) {
    const formData = new FormData();
    formData.append("file", file);
    return this.request<Document>(`/documents/upload/${taxReturnId}`, {
      method: "POST",
      body: formData,
    });
  }

  async batchUploadDocuments(taxReturnId: string, files: File[]) {
    const formData = new FormData();
    for (const f of files) {
      formData.append("files", f);
    }
    return this.request<BatchUploadResponse>(`/documents/batch/${taxReturnId}`, {
      method: "POST",
      body: formData,
    });
  }

  // Documents — Live-Stream (SSE)
  openDocumentStream(taxReturnId: string): EventSource {
    const token = this.getToken();
    const url = `${API_BASE}/documents/stream/${taxReturnId}?token=${encodeURIComponent(token ?? "")}`;
    return new EventSource(url);
  }

  // Documents — CRUD
  async getDocuments(taxReturnId: string) {
    return this.request<Document[]>(`/documents/tax-return/${taxReturnId}`);
  }

  async getDocument(id: string) {
    return this.request<Document>(`/documents/${id}`);
  }

  async reExtractDocument(id: string) {
    return this.request<Document>(`/documents/${id}/re-extract`, {
      method: "POST",
    });
  }

  async updateExtractedData(id: string, data: Record<string, unknown>) {
    return this.request<Document>(`/documents/${id}/extracted-data`, {
      method: "PATCH",
      body: JSON.stringify(data),
    });
  }

  async acceptDocumentSuggestion(id: string, overrides?: Record<string, number>) {
    return this.request<Document>(`/documents/${id}/accept`, {
      method: "POST",
      body: JSON.stringify({ overrides: overrides ?? {} }),
    });
  }

  async deleteDocument(id: string) {
    return this.request<void>(`/documents/${id}`, {
      method: "DELETE",
    });
  }

  async openDocumentFile(documentId: string) {
    const token = this.getToken();
    const response = await fetch(`${API_BASE}/documents/${documentId}/file`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!response.ok) throw new Error("Datei konnte nicht geladen werden");
    const blob = await response.blob();
    const url = URL.createObjectURL(blob);
    window.open(url, "_blank");
  }

  // Open Questions
  async getOpenQuestions(taxReturnId: string) {
    return this.request<OpenQuestionsResponse>(`/open-questions/${taxReturnId}`);
  }

  async saveOpenAnswers(taxReturnId: string, kachelId: string, answers: Record<string, unknown>) {
    return this.request<{ success: boolean }>(`/open-questions/${taxReturnId}/answers`, {
      method: "POST",
      body: JSON.stringify({ kachel_id: kachelId, answers }),
    });
  }

  // PDF-Export
  async openTaxReturnPdf(taxReturnId: string) {
    const token = this.getToken();
    const response = await fetch(`${API_BASE}/tax-returns/${taxReturnId}/pdf`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: "PDF konnte nicht generiert werden" }));
      throw new Error(error.detail || `HTTP ${response.status}`);
    }
    const blob = await response.blob();
    const url = URL.createObjectURL(blob);
    window.open(url, "_blank");
  }
}

// Types
export interface User {
  id: string;
  email: string;
  full_name: string;
  role: string;
  is_active: boolean;
  mfa_enabled: boolean;
}

export interface Client {
  id: string;
  first_name: string;
  last_name: string;
  date_of_birth: string | null;
  ahv_number: string | null;
  civil_status: string;
  tax_type: string;
  partner_first_name: string | null;
  partner_last_name: string | null;
  street: string | null;
  house_number: string | null;
  postal_code: string | null;
  city: string | null;
  canton: string;
  number_of_children: number;
  email: string | null;
  phone: string | null;
  confession: string | null;
  municipality: string | null;
  notes: string | null;
  created_at: string;
  updated_at: string;
}

export interface TaxReturn {
  id: string;
  client_id: string;
  tax_year: number;
  status: string;

  // Einkünfte (Ziff 1–7)
  employment_income: number | null;
  employment_income_p2: number | null;
  self_employment_income: number | null;
  self_employment_income_p2: number | null;
  side_income: number | null;
  service_compensation: number | null;
  ahv_pension_p1: number | null;
  ahv_pension_p2: number | null;
  rental_income: number | null;
  securities_income: number | null;
  qualified_participations: number | null;
  received_alimony: number | null;
  other_income: number | null;

  // Abzüge
  professional_expenses: number | null;
  debt_interest: number | null;
  alimony_paid: number | null;
  pillar_3a: number | null;
  insurance_premiums: number | null;
  insurance_premiums_federal: number | null;
  ahv_contributions: number | null;
  bvg_buyback: number | null;
  education_costs: number | null;
  disability_costs: number | null;
  party_contributions: number | null;
  childcare_deduction: number | null;
  dual_income_deduction: number | null;
  property_maintenance: number | null;
  securities_losses: number | null;
  other_deductions_federal: number | null;
  other_deductions_canton: number | null;

  // Einkommensberechnung
  net_income_canton: number | null;
  net_income_federal: number | null;
  medical_expenses: number | null;
  charitable_donations: number | null;
  pure_income_canton: number | null;
  pure_income_federal: number | null;
  child_deduction_canton: number | null;
  child_deduction_federal: number | null;
  supported_deduction_canton: number | null;
  supported_deduction_federal: number | null;
  married_deduction_canton: number | null;
  social_deductions_canton: number | null;
  social_deductions_federal: number | null;
  income_other_cantons: number | null;
  income_abroad: number | null;

  // Vermögen
  bank_accounts_total: number | null;
  securities_total: number | null;
  real_estate_value: number | null;
  vehicles_value: number | null;
  other_assets: number | null;
  total_debts: number | null;
  wealth_other_cantons: number | null;
  wealth_abroad: number | null;

  // Berechnete Werte
  taxable_income_canton: number | null;
  taxable_income_federal: number | null;
  taxable_wealth: number | null;
  tax_canton: number | null;
  tax_municipality: number | null;
  tax_church: number | null;
  tax_federal: number | null;
  tax_total: number | null;
  withholding_tax_refund: number | null;

  form_data: Record<string, unknown> | null;
  field_sources: Record<string, FieldSource> | null;
  submitted_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface FieldSource {
  document_id: string;
  document_name: string;
  document_type: string;
  extracted_field: string;
}

export interface SuggestedPosition {
  field: string;
  ziffer: string;
  label: string;
  amount: number;
  confidence: number;
}

export interface DocumentSuggestion {
  primary_position: SuggestedPosition | null;
  secondary_positions: SuggestedPosition[];
  reasoning: string;
  needs_review: boolean;
  review_reason: string | null;
}

export interface Document {
  id: string;
  tax_return_id: string;
  original_filename: string;
  file_size: number;
  mime_type: string;
  page_count: number | null;
  content_hash: string | null;
  document_type: string;
  classification_confidence: number | null;
  extraction_status: string;
  extracted_data: Record<string, unknown> | null;
  extraction_confidence: number | null;
  extraction_errors: string[] | null;
  suggestion: DocumentSuggestion | null;
  accepted: boolean;
  created_at: string;
}

export interface BatchUploadResponse {
  tax_return_id: string;
  queued: Document[];
  duplicates: Document[];
  rejected: Array<{ filename: string; reason: string }>;
}

export const api = new ApiClient();

export interface OpenQuestion {
  id: string;
  label: string;
  type: "choice" | "number" | "boolean" | "text";
  options?: { value: string; label: string }[];
  required?: boolean;
  default?: unknown;
  hint?: string;
  placeholder?: string;
  required_if?: Record<string, string[]>;
}

export interface QuestionKachel {
  kachel_id: string;
  titel: string;
  subtitle?: string;
  icon?: string;
  fragen: OpenQuestion[];
  beantwortet: number;
  total: number;
  status: "offen" | "teilweise" | "vollstaendig";
}

export interface OpenQuestionsResponse {
  tax_return_id: string;
  kacheln: QuestionKachel[];
  gesamt_offen: number;
  gesamt_total: number;
}
